import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Check, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import PropertyCard from "@/components/PropertyCard";
import { formatPrice } from "@/lib/format";

export default function ProjectDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [units, setUnits] = useState([]);
  const [active, setActive] = useState(0);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setActive(0);
    api
      .get(`/projects/${id}`)
      .then((r) => {
        setItem(r.data.item);
        setUnits(r.data.units);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [id]);

  if (state === "loading") {
    return (
      <div className="container-lux py-16 space-y-6" data-testid="project-loading">
        <div className="h-[420px] animate-pulse rounded-2xl bg-forest/5" />
        <div className="h-8 w-1/3 animate-pulse rounded bg-forest/5" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="container-lux py-32 flex flex-col items-center gap-4 text-center" data-testid="project-not-found">
        <h1 className="font-heading font-bold text-2xl text-forest">هذا المشروع غير موجود</h1>
        <Link to="/projects" className="btn-gold text-sm" data-testid="back-to-projects">
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
          العودة للمشاريع
        </Link>
      </div>
    );
  }

  const images = item.images || [];

  return (
    <>
      <Seo title={item.name} description={`${item.name} — ${item.city}. مشروع سكني افتراضي لأغراض العرض التجريبي.`} />
      <section className="container-lux py-10 md:py-14" data-testid="project-detail">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-4">
          <div className="relative h-[320px] md:h-[460px] overflow-hidden rounded-3xl">
            <img src={images[active]} alt={`${item.name} — صورة ${active + 1}`} className="h-full w-full object-cover" data-testid="project-main-image" />
            <span className="absolute top-5 start-5 rounded-full bg-gold px-5 py-2 text-sm font-heading font-bold text-forest" data-testid="project-status-badge">{item.status}</span>
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`عرض الصورة ${i + 1}`}
                  data-testid={`project-thumb-${i}`}
                  className={`h-20 w-28 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === active ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
          <div className="lg:col-span-8 space-y-10">
            <div className="space-y-3">
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                {item.location || item.city}
              </p>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-forest tracking-tight">{item.name}</h1>
              {item.starting_price && (
                <p className="font-heading font-extrabold text-2xl text-gold-dark" data-testid="project-price">تبدأ الأسعار من {formatPrice(item.starting_price)}</p>
              )}
            </div>
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-2xl text-forest">عن المشروع</h2>
              <p className="leading-relaxed text-charcoal/80" data-testid="project-description">{item.description}</p>
            </div>
            {item.unit_types?.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-2xl text-forest">أنواع الوحدات</h2>
                <div className="flex flex-wrap gap-3" data-testid="project-unit-types">
                  {item.unit_types.map((t) => (
                    <span key={t} className="rounded-full bg-forest px-5 py-2 text-sm font-heading font-semibold text-ivory">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {item.features?.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-2xl text-forest">مرافق المشروع</h2>
                <ul className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="project-features">
                  {item.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-sm shadow-[0_10px_40px_rgba(10,42,26,0.04)]">
                      <Check className="h-4 w-4 text-gold-dark shrink-0" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-5">
              <div className="rounded-2xl bg-forest p-7 text-ivory space-y-5">
                <h3 className="font-heading font-bold text-xl">سجّل اهتمامك</h3>
                <p className="text-sm text-ivory/70 leading-relaxed">أخبرنا بنوع الوحدة التي تبحث عنها داخل هذا المشروع.</p>
                <Link to="/request" className="btn-gold w-full" data-testid="project-request-button">اطلب وحدة في المشروع</Link>
                <p className="text-[11px] text-gold/80 leading-relaxed border-t border-ivory/10 pt-4">مشروع افتراضي ضمن نموذج تجريبي — ليس طرحاً حقيقياً للبيع.</p>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-4 text-xs text-charcoal/80 leading-relaxed" data-testid="project-demo-note">
                <AlertTriangle className="h-4 w-4 text-gold-dark shrink-0 mt-0.5" aria-hidden="true" />
                نموذج تجريبي: بيانات المشروع والأسعار والصور افتراضية لأغراض العرض فقط.
              </div>
            </div>
          </aside>
        </div>

        {units.length > 0 && (
          <div className="mt-20 space-y-8" data-testid="project-units">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-forest">وحدات مرتبطة بالمشروع</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {units.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
